import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adminApi,
  type SportsVodResolveCacheRow,
  type SportsVodResolveStatus,
} from '@/services/adminApi';
import { formatRelativeTime } from '@/i18n/relativeTime';

const STATUS_OPTIONS: Array<{ value: '' | SportsVodResolveStatus; labelKey: string }> = [
  { value: '', labelKey: 'admin.resolveCache.statusAll' },
  { value: 'fresh', labelKey: 'admin.resolveCache.statusFresh' },
  { value: 'pending', labelKey: 'admin.resolveCache.statusPending' },
  { value: 'failed', labelKey: 'admin.resolveCache.statusFailed' },
  { value: 'unwatchable', labelKey: 'admin.resolveCache.statusUnwatchable' },
];

function StatusBadge({ status }: { status: SportsVodResolveStatus }): JSX.Element {
  return <span className={`time-pill status-pill--${status}`} data-testid={`cache-status-${status}`}>{status}</span>;
}

/**
 * Admin view of the SportsVodResolveCache table. Watchwrestling's pre-resolve
 * executor populates this; rows are keyed by SportsVodTitle.id.
 *
 *   - Filter by status (fresh / pending / failed / unwatchable / All)
 *   - Per-row "Re-resolve" jumps the priority queue (forces a fresh attempt)
 *   - Per-row "Unhide" promotes status='unwatchable' back to 'pending' so it
 *     reappears in the catalog browse and gets re-queued by the cron picker
 */
export function SportsVodResolveCachePage(): JSX.Element {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SportsVodResolveCacheRow[]>([]);
  const [status, setStatus] = useState<'' | SportsVodResolveStatus>('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await adminApi.listSportsVodResolveCache({ status: status || undefined });
      setRows(res.rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('admin.resolveCache.loadError'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleReResolve(titleId: string): Promise<void> {
    setBusyId(titleId);
    try {
      await adminApi.reResolveSportsVodTitle(titleId);
      // Optimistically flip the row to 'pending' so the UI reflects the
      // intent before the next list refresh lands.
      setRows((prev) => prev.map((r) =>
        r.sportsVodTitleId === titleId ? { ...r, status: 'pending', lastAttemptedAt: new Date().toISOString() } : r,
      ));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('admin.resolveCache.reResolveError'));
    } finally {
      setBusyId(null);
    }
  }

  async function handleUnhide(titleId: string): Promise<void> {
    setBusyId(titleId);
    try {
      const { row } = await adminApi.unhideSportsVodTitle(titleId);
      setRows((prev) => prev.map((r) => (r.sportsVodTitleId === titleId ? row : r)));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('admin.resolveCache.unhideError'));
    } finally {
      setBusyId(null);
    }
  }

  function toggleExpand(titleId: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(titleId)) next.delete(titleId);
      else next.add(titleId);
      return next;
    });
  }

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('admin.resolveCache.title')}</h1>
        <p className="muted">
          {t('admin.resolveCache.blurb')}
        </p>
      </header>

      <section className="admin-card">
        <div className="admin-card__row">
          <h2 className="admin-card__title">{t('admin.resolveCache.cacheRows')}</h2>
          <div className="admin-card__row" style={{ gap: '0.5rem' }}>
            <label htmlFor="cache-status-filter" className="muted">{t('admin.resolveCache.statusLabel')}</label>
            <select
              id="cache-status-filter"
              value={status}
              onChange={(e): void => setStatus(e.target.value as '' | SportsVodResolveStatus)}
              data-testid="cache-status-filter"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn"
              onClick={() => void refresh()}
              disabled={loading}
              data-testid="cache-refresh"
            >
              {loading ? t('admin.resolveCache.loading') : t('admin.resolveCache.refresh')}
            </button>
          </div>
        </div>
        {loadError && <p className="error" role="alert">{loadError}</p>}
        {!loading && rows.length === 0 && <p className="muted">{t('admin.resolveCache.noRows')}</p>}
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">{t('admin.resolveCache.colTitleId')}</th>
                <th scope="col">{t('admin.resolveCache.colStatus')}</th>
                <th scope="col">{t('admin.resolveCache.colResolved')}</th>
                <th scope="col">{t('admin.resolveCache.colLastAttempt')}</th>
                <th scope="col" className="num">{t('admin.resolveCache.colFails')}</th>
                <th scope="col" aria-label={t('admin.common.actions')} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isExpanded = expanded.has(row.sportsVodTitleId);
                const rowBusy = busyId === row.sportsVodTitleId;
                return (
                  <Fragment key={row.sportsVodTitleId}>
                    <tr data-testid={`cache-row-${row.sportsVodTitleId}`}>
                      <td><code>{row.sportsVodTitleId}</code></td>
                      <td><StatusBadge status={row.status} /></td>
                      <td>{formatRelativeTime(t, row.resolvedAt, nowMs)}</td>
                      <td>{formatRelativeTime(t, row.lastAttemptedAt, nowMs)}</td>
                      <td className="num">{row.failCount}</td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => void handleReResolve(row.sportsVodTitleId)}
                          disabled={rowBusy}
                          aria-label={t('admin.resolveCache.reResolveAria', { id: row.sportsVodTitleId })}
                        >
                          {rowBusy ? t('admin.resolveCache.working') : t('admin.resolveCache.reResolve')}
                        </button>
                        {row.status === 'unwatchable' && (
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => void handleUnhide(row.sportsVodTitleId)}
                            disabled={rowBusy}
                            aria-label={t('admin.resolveCache.unhideAria', { id: row.sportsVodTitleId })}
                          >
                            {rowBusy ? t('admin.resolveCache.working') : t('admin.resolveCache.unhide')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => toggleExpand(row.sportsVodTitleId)}
                          aria-expanded={isExpanded}
                          aria-controls={`cache-details-${row.sportsVodTitleId}`}
                          aria-label={isExpanded ? t('admin.common.hideDetails', { name: row.sportsVodTitleId }) : t('admin.common.showDetails', { name: row.sportsVodTitleId })}
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr id={`cache-details-${row.sportsVodTitleId}`} className="admin-redemptions-row">
                        <td colSpan={6}>
                          <dl className="health-details">
                            <dt>{t('admin.resolveCache.dmIds')}</dt>
                            <dd>{row.dmIds.length ? <code>{row.dmIds.join(', ')}</code> : <span className="text-dim">—</span>}</dd>
                            <dt>{t('admin.resolveCache.iframeUrl')}</dt>
                            <dd>{row.iframeUrl ? <code>{row.iframeUrl}</code> : <span className="text-dim">—</span>}</dd>
                            <dt>{t('admin.resolveCache.lastError')}</dt>
                            <dd>{row.lastError ? <code>{row.lastError}</code> : <span className="text-dim">—</span>}</dd>
                          </dl>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
